/**
 * Utility to extract, filter, and format company accounts from enterprise user type data.
 *
 * Requirements:
 * 1. Filter only 'company' accounts (excluding universities and other non-company organizations).
 * 2. Filter out older/duplicate company entries with the same name, keeping the latest active record.
 * 3. Format response:
 *    - entityId -> id
 *    - roleName -> role
 *    - uniqueId -> cid
 *    - organizationName -> companyName
 *    - organizationLogo -> companyLogo
 *    - use id as key to map and render
 */

/**
 * Extracts the organizations array from various possible userTypeData payload structures.
 *
 * @param {Array|Object} userTypeData - Redux state or raw API response
 * @returns {Array} List of raw organization objects
 */
export const extractOrganizations = (userTypeData) => {
  if (!userTypeData) return [];

  // If userTypeData is an array
  if (Array.isArray(userTypeData)) {
    // Array of user objects containing organizations: [{ userId, organizations: [...] }]
    if (userTypeData.length > 0 && userTypeData[0]?.organizations) {
      return userTypeData.flatMap((item) => item.organizations || []);
    }
    // Array of organizations directly: [{ entityId, organizationName, ... }]
    if (
      userTypeData.length > 0 &&
      (userTypeData[0]?.organizationType || userTypeData[0]?.entityId || userTypeData[0]?.uniqueId)
    ) {
      return userTypeData;
    }
    return [];
  }

  // If userTypeData is wrapped in { data: ... }
  if (userTypeData.data) {
    return extractOrganizations(userTypeData.data);
  }

  // If single user object with organizations: { organizations: [...] }
  if (Array.isArray(userTypeData.organizations)) {
    return userTypeData.organizations;
  }

  return [];
};

/**
 * Filters out non-companies and duplicate/old company entries, then formats the output.
 *
 * @param {Array|Object} userTypeData - Raw user type data or Redux state
 * @returns {Array<Object>} Formatted company list
 */
export const filterCompaniesFromUserType = (userTypeData, experiences = []) => {
  const organizations = extractOrganizations(userTypeData);
  if (!organizations || organizations.length === 0) return [];

  // Normalize experiences array
  const expList = Array.isArray(experiences)
    ? experiences
    : experiences?.data && Array.isArray(experiences.data)
    ? experiences.data
    : [];

  // Step 1: Filter only organizations of type "company"
  const companyOrgs = organizations.filter((org) => {
    const orgType = org?.organizationType?.toLowerCase()?.trim();
    return orgType === "company";
  });

  // Step 2: Deduplicate and filter out older company records for the same company name.
  // We keep the entry with the highest entityId or highest CID number (latest record).
  const latestCompaniesByName = new Map();

  for (const org of companyOrgs) {
    const rawName = org?.organizationName?.trim();
    if (!rawName) continue;

    const normalizedKey = rawName.toLowerCase();
    const existing = latestCompaniesByName.get(normalizedKey);

    if (!existing) {
      latestCompaniesByName.set(normalizedKey, org);
    } else {
      // Determine which record is newer (higher entityId or higher CID)
      const existingEntityId = Number(existing.entityId) || 0;
      const currentEntityId = Number(org.entityId) || 0;

      if (currentEntityId >= existingEntityId) {
        latestCompaniesByName.set(normalizedKey, org);
      }
    }
  }

  // Step 3: Format the response according to specification:
  // entityId -> id
  // roleName -> role
  // uniqueId -> cid
  // organizationName -> companyName
  // organizationLogo -> companyLogo
  const formattedCompanies = Array.from(latestCompaniesByName.values()).map((org) => {
    const entityId = org?.entityId;
    const roleName = org?.roleName || "Admin";
    const cid = org?.uniqueId || org?.uniqueid || "";
    const companyName = org?.organizationName || "";
    const companyLogo = org?.organizationLogo || "";

    // Find matching experience from experiences list if provided
    const targetName = companyName.trim().toLowerCase();
    const targetCid = cid.trim().toLowerCase();
    const orgLogo = companyLogo?.trim();

    const matchedExp = expList.find((exp) => {
      const expCid = (exp?.cid || exp?.uniqueId || exp?.company_id || "")?.toString()?.trim()?.toLowerCase();
      const expName = (exp?.company_name || exp?.companyName || "")?.trim()?.toLowerCase();
      const expLogo = (exp?.company_logo || exp?.companylogo?.data || "")?.toString()?.trim();

      // 1. Primary: Match by exact CID / unique enterprise ID
      if (expCid && targetCid && expCid === targetCid) return true;

      // 2. Exact match by normalized company name
      if (expName && targetName && expName === targetName) return true;

      // 3. Match by exact logo URL (unique blob storage URL per company)
      if (expLogo && orgLogo && expLogo === orgLogo) return true;

      // 4. Substring name fallback (e.g. "Dalocal" vs "Dalocal Technologies Private Limited")
      if (expName && targetName && (expName.includes(targetName) || targetName.includes(expName))) return true;

      return false;
    });

    let companyDomain =
      org?.company_domain ||
      org?.companyDomain ||
      org?.organizationDomain ||
      org?.domain ||
      org?.website ||
      org?.company_url ||
      matchedExp?.company_domain ||
      matchedExp?.companyDomain ||
      matchedExp?.website ||
      matchedExp?.company_url ||
      "";

    // Fallback: If company_domain wasn't directly present, extract from corporate email (e.g. pooja@dalocal.co -> dalocal.co)
    if (!companyDomain && matchedExp?.company_email && matchedExp.company_email.includes("@")) {
      const emailDomain = matchedExp.company_email.split("@")[1]?.trim()?.toLowerCase();
      const publicProviders = ["gmail.com", "yahoo.com", "outlook.com", "hotmail.com", "icloud.com"];
      if (emailDomain && !publicProviders.includes(emailDomain)) {
        companyDomain = emailDomain;
      }
    }

    return {
      id: entityId,
      role: roleName,
      cid: cid,
      companyName: companyName,
      companyLogo: companyLogo,
      company_domain: companyDomain,
      companyDomain: companyDomain,
      company_url: companyDomain,
      website: companyDomain,
      // Default location & status for modal rendering (matching verified professional view)
      location: matchedExp?.location || "Remote",
      is_verified: true,
      is_currently_working: true,
      // Backward-compatibility aliases
      entityId: entityId,
      roleName: roleName,
      uniqueId: cid,
      company_name: companyName,
      company_logo: companyLogo,
    };
  });

  return formattedCompanies;
};

// Aliases
export const filterCompanyUtil = filterCompaniesFromUserType;
export const filterOldCompanies = filterCompaniesFromUserType;
export const formatCompanyResponse = filterCompaniesFromUserType;

export default filterCompaniesFromUserType;
